"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Cell, Pie, PieChart } from "recharts";
import { cn } from "../../lib/utils";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";

// CONTAINER STYLING
const chartContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-2xl bg-card p-4 text-card-foreground ring-1 ring-foreground/10",
          "hover:ring-foreground/20",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "card",
      size: "md",
    },
  }
);

// TITLE STYLING
const chartTitleVariants = cva("font-semibold tracking-tight", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// DESCRIPTION STYLING
const chartDescriptionVariants = cva("text-muted-foreground leading-relaxed", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface RadialChartItemProps
  extends VariantProps<typeof chartContainerVariants> {
  // Accessibility
  ariaLabel?: string;
  aspectRatio?: "video" | "square" | "auto";

  // Styling
  className?: string;
  classNames?: {
    container?: string;
    header?: string;
    title?: string;
    description?: string;
    chart?: string;
  };
  config: ChartConfig;

  // Chart configuration
  data: Record<string, unknown>[];
  dataKey?: string;

  // Chart options
  description?: string;

  // Chart dimensions
  height?: number | string;

  // Donut hole size — number (px) or percentage string e.g. "55%"
  innerRadius?: number | string;

  // Which field is the slice name and which is the value
  nameKey?: string;

  // Interaction
  onClick?: () => void;
  // Outer radius — number (px) or percentage string e.g. "80%"
  outerRadius?: number | string;
  showLegend?: boolean;
  showTooltip?: boolean;
  size?: "sm" | "md" | "lg";

  // Labels
  title?: string;

  // Styling variants
  variant?: "card" | "outline" | "ghost" | "minimal";
}

export function RadialChartItem({
  data,
  config,
  nameKey = "name",
  dataKey = "value",
  innerRadius = "55%",
  outerRadius = "80%",
  showLegend = false,
  showTooltip = true,
  title,
  description,
  variant = "card",
  size = "md",
  height,
  aspectRatio = "square",
  onClick,
  ariaLabel,
  className,
  classNames,
}: RadialChartItemProps) {
  const isClickable = Boolean(onClick);

  const containerClassName = cn(
    chartContainerVariants({ variant, size }),
    isClickable && "cursor-pointer",
    classNames?.container,
    className
  );

  const chartClassName = cn(
    aspectRatio === "video" && "aspect-video",
    aspectRatio === "square" && "mx-auto aspect-square w-full max-w-[280px]",
    aspectRatio === "auto" && "w-full",
    classNames?.chart
  );

  const chartAriaLabel =
    ariaLabel ?? (title ? `${title} chart` : "Donut Chart");

  const configKeys = Object.keys(config);

  // Pre-compute per-slice fills so JSX map doesn't need index as a key
  const cellFills = data.map((entry, i) => {
    const entryName = entry[nameKey] as string | undefined;
    const configKey =
      entryName && config[entryName]
        ? entryName
        : (configKeys[i % configKeys.length] ?? configKeys[0] ?? "");
    // Read color directly from config — avoids CSS var name issues with special characters
    return {
      fill:
        (config[configKey]?.color as string | undefined) ??
        `var(--chart-${(i % 5) + 1})`,
      key: String(entryName ?? configKey),
    };
  });

  const renderChart = () => (
    <PieChart accessibilityLayer>
      {showTooltip && (
        <ChartTooltip content={<ChartTooltipContent nameKey={nameKey} />} />
      )}
      <Pie
        data={data}
        dataKey={dataKey}
        innerRadius={innerRadius}
        nameKey={nameKey}
        outerRadius={outerRadius}
        strokeWidth={2}
      >
        {cellFills.map((cell) => (
          <Cell fill={cell.fill} key={cell.key} />
        ))}
      </Pie>
    </PieChart>
  );

  const content = (
    <>
      {(title ?? description) && (
        <div
          className={cn(
            "flex flex-col items-start justify-between gap-1",
            classNames?.header
          )}
        >
          {title && (
            <div
              className={cn(chartTitleVariants({ size }), classNames?.title)}
            >
              {title}
            </div>
          )}
          {description && (
            <div
              className={cn(
                chartDescriptionVariants({ size }),
                classNames?.description
              )}
            >
              {description}
            </div>
          )}
        </div>
      )}

      <ChartContainer
        aria-label={chartAriaLabel}
        className={chartClassName}
        config={config}
        style={height ? { height } : undefined}
      >
        {renderChart()}
      </ChartContainer>

      {showLegend && (
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 pt-1 text-sm">
          {cellFills.map((cell, i) => {
            const configKey = Object.keys(config)[i] ?? "";
            const label = config[configKey]?.label ?? configKey;
            return (
              <div className="flex items-center gap-1.5" key={cell.key}>
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: cell.fill }}
                />
                {label}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (isClickable) {
    return (
      <button className={containerClassName} onClick={onClick} type="button">
        {content}
      </button>
    );
  }

  return <div className={containerClassName}>{content}</div>;
}

export interface RadialChartCollectionProps
  extends VariantProps<typeof chartContainerVariants> {
  className?: string;
  columns?: 1 | 2 | 3;
  gap?: "sm" | "md" | "lg";
  items: RadialChartItemProps[];
  layout?: "stack" | "grid";
  onItemClick?: (item: RadialChartItemProps, index: number) => void;
  size?: "sm" | "md" | "lg";
  variant?: "card" | "outline" | "ghost" | "minimal";
}

const chartCollectionVariants = cva("w-full", {
  variants: {
    layout: {
      stack: "flex flex-col",
      grid: "grid",
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-1 md:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    },
    gap: {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    layout: "stack",
    gap: "md",
  },
});

export function RadialChartCollection({
  items,
  layout = "stack",
  columns = 1,
  gap = "md",
  variant = "card",
  size = "md",
  onItemClick,
  className,
}: RadialChartCollectionProps) {
  return (
    <div
      className={cn(
        chartCollectionVariants({
          layout,
          columns: layout === "grid" ? columns : undefined,
          gap,
        }),
        className
      )}
    >
      {items.map((item, index) => (
        <RadialChartItem
          key={item.title ?? `radial-chart-${index}`}
          onClick={
            onItemClick
              ? () => onItemClick(item as RadialChartItemProps, index)
              : item.onClick
          }
          size={item.size ?? size}
          variant={item.variant ?? variant}
          {...item}
        />
      ))}
    </div>
  );
}
